<template>
  <div class="ExtractTemplate">
    <Loader text="Loading Extract Template" v-if="!Template" />
    <div>
      <header class="ExtractTemplate__header">
        <div
          class="ExtractTemplate__header-container d-flex justify-space-between align-center"
        >
          <div class="d-flex align-center">
            <p class="ExtractTemplate__header-title">
              <router-link
                to="/extract-templates"
                tag="span"
                class="ExtractTemplate__header-title-link"
              >
                Extract Templates
              </router-link>
              <v-icon small class="ExtractTemplate__header-title-icon">
                mdi-chevron-right
              </v-icon>
              <span class="ExtractTemplate__header-title-name" v-if="Template.name">
                {{ Template.name }}
              </span>
              <span class="ExtractTemplate__header-title-no-name" v-else>
                Template name here
              </span>
            </p>
          </div>

          <div class="d-flex justify-space-between">
            <div class="ExtractTemplate__header-button-divider"></div>
            <v-btn
              color="primary"
              elevation="0"
              :disabled="updateTemplateLoading"
              @click="save"
              :loading="updateTemplateLoading"
            >
              Save Template
            </v-btn>
          </div>
        </div>
      </header>

      <div class="ExtractTemplate__main-content">
        <v-row>
          <v-col cols="12" lg="8">
            <v-card class="TemplateSettings" :elevation="1">
              <v-card-title class="d-flex align-center">
                <v-icon left color="primary">mdi-code-json</v-icon>
                Extract Template Settings
              </v-card-title>
              <v-card-text>
                <v-form ref="templateForm" v-model="formValid">
                  <div class="mb-6">
                    <v-text-field
                      v-model="localTemplate.name"
                      label="Template Name"
                      placeholder="e.g., Invoice Data, Medical Report, Contract Details"
                      outlined
                      :rules="nameRules"
                      class="mb-3"
                    />

                    <v-textarea
                      v-model="localTemplate.description"
                      label="Template Description"
                      placeholder="Describe what data this template extracts from documents..."
                      outlined
                      rows="3"
                      :rules="descriptionRules"
                    />
                  </div>

                  <v-divider class="mb-6"></v-divider>

                  <div class="mb-6">
                    <div class="d-flex align-center mb-4">
                      <div class="text-h6">Schema Structure</div>
                      <v-spacer></v-spacer>
                      <v-chip small :color="Template.customer_id ? 'blue' : 'green'" dark>
                        {{ Template.customer_id ? 'Customer Template' : 'System Template' }}
                      </v-chip>
                    </div>

                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-3">
                        <div class="text-subtitle1">Template Fields</div>
                        <v-btn
                          small
                          color="primary"
                          outlined
                          @click="addField"
                          :disabled="isSystemTemplate(Template)"
                        >
                          <v-icon left small>mdi-plus</v-icon>
                          Add Field
                        </v-btn>
                      </div>

                      <div v-if="templateFields.length === 0" class="text-center py-6">
                        <v-icon size="48" color="grey lighten-1">mdi-format-list-bulleted</v-icon>
                        <p class="text-subtitle2 grey--text mt-2">No fields defined yet</p>
                        <p class="text-caption grey--text">Click "Add Field" to start building your template</p>
                      </div>

                      <div v-else>
                          <div v-for="(field, fieldName, index) in localTemplate.schema.properties"
                               :key="`field-${index}`"
                               class="mb-4">
                            <extract-field-editor
                              :field-name="fieldName"
                              :field="field"
                              :index="index"
                              :is-required="isRequiredField(fieldName)"
                              :parent-path="''"
                              @update-field="updateField"
                              @update-field-name="updateFieldName"
                              @remove-field="removeField"
                              @toggle-required="toggleRequired"
                            />
                          </div>
                      </div>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <v-expansion-panels flat>
                      <v-expansion-panel>
                        <v-expansion-panel-header class="pa-3">
                          <div class="d-flex align-center">
                            <v-icon left small>mdi-code-json</v-icon>
                            <span class="text-subtitle2">JSON Schema Preview</span>
                            <v-spacer></v-spacer>
                            <span class="text-caption grey--text">
                              {{ Object.keys(localTemplate.schema.properties || {}).length }} fields
                            </span>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-card outlined class="pa-3" style="background-color: #f8f9fa;">
                            <pre class="text-caption schema-preview">{{ formattedSchema }}</pre>
                          </v-card>
                          <div class="mt-3">
                            <v-btn
                              text
                              small
                              color="primary"
                              @click="toggleJsonEditor"
                            >
                              <v-icon left small>mdi-pencil</v-icon>
                              {{ showJsonEditor ? 'Hide' : 'Edit' }} Raw JSON
                            </v-btn>
                          </div>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Raw JSON Editor -->
                    <div v-if="showJsonEditor" class="mt-4">
                      <v-card outlined>
                        <v-card-text>
                          <v-textarea
                            v-model="jsonEditorValue"
                            outlined
                            label="JSON Schema (Advanced Editing)"
                            rows="12"
                            :error-messages="jsonEditorError"
                            class="json-editor"
                          />
                          <div class="d-flex gap-2">
                            <v-btn
                              small
                              color="primary"
                              @click="applyJsonChanges"
                              :disabled="!!jsonEditorError"
                            >
                              Apply Changes
                            </v-btn>
                            <v-btn
                              small
                              text
                              @click="cancelJsonEditor"
                            >
                              Cancel
                            </v-btn>
                          </div>
                        </v-card-text>
                      </v-card>
                    </div>
                  </div>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" lg="4">
            <v-card class="TestWidget TestWidget--sticky" :elevation="1">
              <v-card-title>Test Extract Template</v-card-title>
              <v-card-text>
                <div class="mb-4">
                  <v-file-input
                    v-model="testFile"
                    label="Upload PDF File"
                    accept=".pdf"
                    outlined
                    prepend-icon="mdi-file-pdf-box"
                    :loading="fileProcessing"
                    @change="handleFileUpload"
                    show-size
                  />
                </div>

                <v-btn
                  color="primary"
                  block
                  elevation="0"
                  :disabled="!testFileBase64 || testLoading"
                  :loading="testLoading"
                  @click="runTest"
                >
                  TEST
                </v-btn>

                <div v-if="testResult" class="mt-4">
                  <v-alert
                    :type="testResult.success ? 'success' : 'error'"
                    dense
                    text
                  >
                    Test {{ testResult.success ? 'Completed' : 'Failed' }}
                  </v-alert>
                  <div v-if="testResult.message" class="mt-2">
                    <v-textarea
                      :value="testResult.message"
                      label="Test Result"
                      readonly
                      outlined
                      dense
                      rows="3"
                    />
                  </div>
                  <div v-if="testResult.extractedData" class="mt-2">
                    <div class="d-flex align-center mb-2">
                      <v-icon left small>mdi-code-json</v-icon>
                      <span class="text-subtitle2">Extracted Data:</span>
                    </div>
                    <json-viewer
                      v-if="testResult.extractedData !== undefined && testResult.extractedData !== ''"
                      :value="testResult.extractedData"
                      :expand-depth="0"
                      boxed
                      sort
                      copyable
                    />
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import JsonViewer from 'vue-json-viewer';
import Loader from '@/components/loader/loader.vue';
import ExtractFieldEditor from '@/components/connector-settings/ai-service/ExtractFieldEditor.vue';

const { mapGetters: templatesGetters, mapActions: templatesActions } =
    createNamespacedHelpers('extractTemplates');
const { mapActions: workflowDetailsActions } =
    createNamespacedHelpers('workflows/details');

export default {
  name: 'ExtractTemplateDetail',
  components: {
    Loader,
    ExtractFieldEditor,
    JsonViewer,
  },
  data() {
    return {
      formValid: false,
      showJsonEditor: false,
      jsonEditorValue: '',
      jsonEditorError: null,
      localTemplate: {
        name: '',
        description: '',
        schema: {
          type: 'object',
          properties: {},
          required: []
        }
      },
      nameRules: [
        v => !!v || 'Template name is required',
      ],
      descriptionRules: [
        v => !!v || 'Description is required',
      ],
      // Test widget data
      testFile: null,
      testFileBase64: null,
      fileProcessing: false,
      testLoading: false,
      testResult: null,
      checkStatusInterval: undefined,
      checkStatusData: {
        action: {},
        task: {}
      },
    };
  },
  computed: {
    ...templatesGetters({
      Template: 'TEMPLATE',
      updateTemplateLoading: 'UPDATE_TEMPLATE_LOADING',
      fetchTemplateLoading: 'FETCH_TEMPLATE_LOADING',
    }),
    templateId() {
      return this.$route.params.templateId;
    },
    formattedSchema() {
      return JSON.stringify(this.localTemplate.schema, null, 2);
    },
    templateFields() {
      if (!this.localTemplate.schema || !this.localTemplate.schema.properties) {
        return [];
      }

      return Object.entries(this.localTemplate.schema.properties).map(([name, field]) => ({
        name,
        ...field,
        required: (this.localTemplate.schema.required || []).includes(name)
      }));
    },
  },
  watch: {
    Template: {
      handler(newTemplate) {
        if (newTemplate) {
          this.localTemplate = {
            name: newTemplate.name || '',
            description: newTemplate.description || '',
            schema: newTemplate.schema || {
              type: 'object',
              properties: {},
              required: []
            }
          };
        }
      },
      immediate: true,
      deep: true,
    },
    jsonEditorValue(newVal) {
      this.validateJson(newVal);
    }
  },
  mounted() {
    if (this.templateId) {
      this.fetchTemplate(this.templateId);
    }
  },
  methods: {
    ...templatesActions(['fetchTemplate', 'updateTemplate']),
    ...workflowDetailsActions(['executeSingleAction', 'getWorkflowTask']),
    async save() {
      if (!this.$refs.templateForm.validate()) {
        return;
      }

      try {
        await this.updateTemplate({
          templateId: this.templateId,
          templateData: {
            name: this.localTemplate.name,
            description: this.localTemplate.description,
            schema: JSON.parse(JSON.stringify(this.localTemplate.schema)),
          },
        });
      } catch (error) {
        console.error('Error saving template:', error);
      }
    },
    toggleJsonEditor() {
      if (!this.showJsonEditor) {
        this.jsonEditorValue = this.formattedSchema;
      }
      this.showJsonEditor = !this.showJsonEditor;
    },
    validateJson(value) {
      this.jsonEditorError = null;
      if (value) {
        try {
          JSON.parse(value);
        } catch (error) {
          this.jsonEditorError = 'Invalid JSON format';
        }
      }
    },
    applyJsonChanges() {
      if (this.jsonEditorError) return;

      try {
        this.localTemplate.schema = JSON.parse(this.jsonEditorValue);
        this.showJsonEditor = false;
      } catch (error) {
        this.jsonEditorError = 'Invalid JSON format';
      }
    },
    cancelJsonEditor() {
      this.showJsonEditor = false;
      this.jsonEditorValue = '';
      this.jsonEditorError = null;
    },
    isSystemTemplate(template) {
      return template && template.customerId === null;
    },
    addField() {
      const newField = {
        name: `field_${Date.now()}`,
        type: 'string',
        description: '',
        required: false
      };

      if (!this.localTemplate.schema.properties) {
        this.localTemplate.schema.properties = {};
      }
      if (!this.localTemplate.schema.required) {
        this.localTemplate.schema.required = [];
      }

      this.$set(this.localTemplate.schema.properties, newField.name, {
        type: newField.type,
        description: newField.description
      });

      this.localTemplate = { ...this.localTemplate };
    },
    updateField(index, updatedField) {
      const fieldName = this.templateFields[index].name;

      this.$set(this.localTemplate.schema.properties, fieldName, {
        type: updatedField.type,
        description: updatedField.description,
        ...(updatedField.type === 'array' && updatedField.items ? { items: updatedField.items } : {}),
        ...(updatedField.type === 'object' && updatedField.properties ? { properties: updatedField.properties } : {})
      });

      this.localTemplate = { ...this.localTemplate };
    },
    toggleRequired(fieldName, isRequired, parentPath = '') {
      if (parentPath) {
        this.toggleNestedRequiredField(this.localTemplate, parentPath, fieldName, isRequired);
      } else {
        let newRequired = [...(this.localTemplate.schema.required || [])];

        if (isRequired && !newRequired.includes(fieldName)) {
          newRequired.push(fieldName);
        } else if (!isRequired) {
          newRequired = newRequired.filter(req => req !== fieldName);
        }

        this.localTemplate.schema = {
          ...this.localTemplate.schema,
          required: newRequired
        };
      }
    },

    isRequiredField(fieldName) {
      return this.localTemplate.schema.required?.includes(fieldName) || false;
    },
    updateFieldName(index, newName) {
      const oldName = this.templateFields[index].name;

      if (oldName === newName) return;

      const fieldDef = this.localTemplate.schema.properties[oldName];

      this.$delete(this.localTemplate.schema.properties, oldName);

      this.$set(this.localTemplate.schema.properties, newName, fieldDef);

      const requiredIndex = this.localTemplate.schema.required.indexOf(oldName);
      if (requiredIndex > -1) {
        this.localTemplate.schema.required.splice(requiredIndex, 1);
        this.localTemplate.schema.required.push(newName);
      }

      this.localTemplate = { ...this.localTemplate };
    },
    removeField(index) {
      const fieldName = this.templateFields[index].name;

      this.$delete(this.localTemplate.schema.properties, fieldName);

      const requiredIndex = this.localTemplate.schema.required.indexOf(fieldName);
      if (requiredIndex > -1) {
        this.localTemplate.schema.required.splice(requiredIndex, 1);
      }

      this.localTemplate = { ...this.localTemplate };
    },
    toggleFieldRequired(index) {
      const fieldName = this.templateFields[index].name;
      const requiredArray = this.localTemplate.schema.required || [];
      const requiredIndex = requiredArray.indexOf(fieldName);

      if (requiredIndex > -1) {
        this.localTemplate.schema.required.splice(requiredIndex, 1);
      } else {
        if (!this.localTemplate.schema.required) {
          this.localTemplate.schema.required = [];
        }
        this.localTemplate.schema.required.push(fieldName);
      }

      this.localTemplate = { ...this.localTemplate };
    },

    // File upload handling
    async handleFileUpload(file) {
      if (!file) {
        this.testFileBase64 = null;
        this.testResult = null;
        return;
      }

      this.fileProcessing = true;
      this.testResult = null;

      try {
        // Convert PDF to base64
        const base64 = await this.convertFileToBase64(file);
        this.testFileBase64 = base64;
      } catch (error) {
        console.error('Error processing file:', error);
        this.testResult = {
          success: false,
          message: 'Error processing PDF file. Please try again.',
        };
      } finally {
        this.fileProcessing = false;
      }
    },

    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // Remove data:application/pdf;base64, prefix to get pure base64
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // Test functionality
    async runTest() {
      if (!this.testFileBase64) {
        return;
      }

      this.testLoading = true;
      this.testResult = null;

      const options = {
        creds: null, // Will use system credentials for AI service
        data: {
          extractTemplate: {
            ...this.localTemplate,
            isCustom: true,
          },
          extract: true,
          visualMode: true,
          file: "Object.file",
        },
        action: 'ai_document_analysis',
        input: {
          file: this.testFileBase64,
        },
        isServerless: true,
      };


      const action = {
        input: {
          file: this.testFileBase64,
        },
      };

      try {
        const data = await this.executeSingleAction(options);
        if (data.success) {
          action.output = data;
          this.testResult = {
            success: true,
            message: 'Data extraction completed successfully',
            extractedData: data,
            taskId: data.taskId
          };
        } else {
          this.testResult = {
            success: false,
            message: data.message || 'Extraction failed',
          };
        }
        this.testLoading = false;
      } catch (error) {
        console.error('Extraction test failed:', error);
        this.testResult = {
          success: false,
          message: error.message || 'Extraction test failed',
        };
        this.testLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ExtractTemplate {
  &__header {
    background: white;
    border-bottom: 1px solid #e0e0e0;
    padding: 16px 24px;

    &-container {
      max-width: 1200px;
      margin: 0 auto;
    }

    &-title {
      font-size: 24px;
      font-weight: 500;
      margin: 0;
      display: flex;
      align-items: center;

      &-link {
        color: #666;
        cursor: pointer;
        text-decoration: none;

        &:hover {
          color: #1976d2;
        }
      }

      &-icon {
        margin: 0 8px;
        color: #666;
      }

      &-name {
        color: #333;
      }

      &-no-name {
        color: #999;
        font-style: italic;
      }
    }

    &-button-divider {
      width: 1px;
      height: 32px;
      background: #e0e0e0;
      margin-right: 16px;
    }
  }

  &__main-content {
    padding: 24px;
    background: #f5f5f5;
    min-height: calc(100vh - 120px);

    .v-row {
      justify-content: center;
    }
  }

  .TemplateSettings {
    .v-card-title {
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .v-card-text {
      padding: 24px;
    }
  }

  .TestWidget {
    .v-card-title {
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .v-card-text {
      padding: 24px;
    }

    &--sticky {
      position: sticky;
      top: 85px;
      max-height: calc(100vh - 120px);
      overflow-y: auto;

      transition: box-shadow 0.2s ease-in-out;

      &:hover {
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12) !important;
      }

      .v-card-text {
        max-height: calc(100vh - 200px);
        overflow-y: auto;
      }
    }
  }
}

.schema-preview {
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.json-editor {
  font-family: 'Courier New', monospace;
}
</style>